/** F5 — detalhe da tarefa: checklist, entrega, anexos, comentários e horas. */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';
import { TipoEntrega } from './enums.js';
import { CompetenciaChave } from './comum.js';
import { TipoGuiaEntrega } from './entregaveis.js';
import { ComposicaoItem, EvidenciaPagamento, SituacaoGuia } from './guia.js';

// ---- checklist ----
export const ChecklistItem = z.object({
  id: Id, ordem: z.number().int(), texto: z.string(), obrigatorio: z.boolean(), feito: z.boolean(),
  feitoPorApelido: z.string().nullable(), feitoEm: z.string().nullable(),
});
export type ChecklistItem = z.infer<typeof ChecklistItem>;

export const ChecklistItemBody = z.object({ texto: z.string().trim().min(1).max(200), obrigatorio: z.boolean().default(false) });
export type ChecklistItemBody = z.infer<typeof ChecklistItemBody>;

export const ChecklistMarcarBody = z.object({ feito: z.boolean() });

/** Modelo por obrigação (a lista inteira é substituída de uma vez). */
export const ChecklistModeloBody = z.object({ itens: z.array(ChecklistItemBody).max(50) });
export type ChecklistModeloBody = z.infer<typeof ChecklistModeloBody>;
export const ChecklistModelo = z.array(z.object({ id: Id, ordem: z.number().int(), texto: z.string(), obrigatorio: z.boolean() }));
export type ChecklistModelo = z.infer<typeof ChecklistModelo>;

// ---- entrega ----
const Valor = z.number().min(0).max(999_999_999_999);
const comum = {
  /** a vaga de "o que a tarefa gera" que esta entrega preenche */
  entregavelChave: z.string().trim().max(40).nullable().optional(),
  anexoId: Id.nullable().default(null),
  observacao: z.string().trim().max(500).nullable().default(null),
};
// ---- importação de guias em PDF
export const TipoGuia = z.enum(['DAS', 'DARF', 'FGTS', 'GPS', 'DAE', 'ISS', 'ICMS', 'DESCONHECIDO']);
export type TipoGuia = z.infer<typeof TipoGuia>;
export const LeituraGuia = z.object({
  tipo: TipoGuia, cnpj: z.string().nullable(), competenciaChave: z.number().int().nullable(), competenciaLabel: z.string().nullable(), vencimento: DataISO.nullable(),
  valorTotal: z.number().nullable(), valorPrincipal: z.number().nullable(), valorMulta: z.number().nullable(), valorJuros: z.number().nullable(),
  codigoReceita: z.string().nullable(), numeroDocumento: z.string().nullable(), linhaDigitavel: z.string().nullable(),
  /** códigos de obrigação do catálogo compatíveis com o documento */
  obrigacoesCandidatas: z.array(z.string()), confianca: z.enum(['ALTA', 'MEDIA', 'BAIXA']), avisos: z.array(z.string()),
});
export type LeituraGuia = z.infer<typeof LeituraGuia>;
export const ImportacaoGuia = z.object({
  arquivo: z.string(), leitura: LeituraGuia,
  sugestao: z.object({ tarefaId: Id, clienteId: Id, clienteNome: z.string(), obrigacaoSigla: z.string(), competenciaLabel: z.string().nullable(), status: z.string() }).nullable(),
  /** já anexado na tarefa sugerida */
  anexoId: Id.nullable(), avisos: z.array(z.string()),
});
export type ImportacaoGuia = z.infer<typeof ImportacaoGuia>;

export const EntregaBody = z.discriminatedUnion('tipo', [
  z.object({
    tipo: z.literal('DECLARACAO'),
    protocolo: z.string().trim().min(1).max(120),
    reciboHash: z.string().trim().max(120).nullable().default(null),
    dataTransmissao: DataISO,
    retificadora: z.boolean().default(false),
    ...comum,
  }),
  z.object({
    tipo: z.literal('GUIA'),
    codigoReceita: z.string().trim().max(20).nullable().default(null),
    valorPrincipal: Valor.nullable().default(null),
    valorMulta: Valor.nullable().default(null),
    valorJuros: Valor.nullable().default(null),
    /** pode faltar: o portal mostra "valor no PDF" */
    valorTotal: Valor.nullable().default(null),
    /** vencimento legal */
    vencimentoGuia: DataISO,
    /** até quando o valor vale (consolidação); quando existe, é a data que o cliente vê */
    pagarAte: DataISO.nullable().default(null),
    linhaDigitavel: z.string().trim().max(80).nullable().default(null),
    codigoBarras: z.string().trim().max(60).nullable().default(null),
    pixCopiaCola: z.string().trim().max(600).nullable().default(null),
    dataPagamento: DataISO.nullable().default(null),
    /** nome como o cliente vê; nulo = pelo tipo e código de receita */
    titulo: z.string().trim().max(120).nullable().default(null),
    tipoGuia: TipoGuiaEntrega.nullable().default(null),
    competenciaChave: CompetenciaChave.nullable().default(null),
    numeroDocumento: z.string().trim().max(60).nullable().default(null),
    cota: z.number().int().min(1).max(12).nullable().default(null),
    composicao: z.array(ComposicaoItem).nullable().default(null),
    ...comum,
  }),
  z.object({
    tipo: z.literal('DOCUMENTO'),
    dataTransmissao: DataISO.nullable().default(null),
    ...comum,
  }),
]);
export type EntregaBody = z.infer<typeof EntregaBody>;

export const Entrega = z.object({
  id: Id, tipo: TipoEntrega,
  protocolo: z.string().nullable(), reciboHash: z.string().nullable(), dataTransmissao: DataISO.nullable(), retificadora: z.boolean(),
  codigoReceita: z.string().nullable(), valorPrincipal: z.number().nullable(), valorMulta: z.number().nullable(), valorJuros: z.number().nullable(), valorTotal: z.number().nullable(),
  vencimentoGuia: DataISO.nullable(), linhaDigitavel: z.string().nullable(), dataPagamento: DataISO.nullable(),
  anexoId: Id.nullable(), observacao: z.string().nullable(), registradoPorApelido: z.string().nullable(), criadoEm: z.string(),
  // ---- a guia como objeto
  titulo: z.string().nullable(), /** o nome como o cliente vê (título, ou pelo tipo e código) */ nomeCliente: z.string(), tipoGuia: z.string().nullable(), competenciaChave: z.number().int().nullable(), competenciaLabel: z.string().nullable(),
  numeroDocumento: z.string().nullable(), codigoBarras: z.string().nullable(), pixCopiaCola: z.string().nullable(), pagarAte: DataISO.nullable(), cota: z.number().int().nullable(), composicao: z.array(ComposicaoItem).nullable(),
  conferidaEm: z.string().nullable(), canceladaEm: z.string().nullable(), substituidaPorId: Id.nullable(), versao: z.number().int(), /** só para guia */ situacao: SituacaoGuia.nullable(),
  /** só para guia (fatia 2a): o que se sabe do pagamento além da data */
  pagamento: z.object({
    evidencia: EvidenciaPagamento, /** PORTAL | LINK | ESCRITORIO | nulo (registro anterior) */ origem: z.string().nullable(),
    informadoEm: z.string().nullable(), /** nome do contato (cliente) ou apelido do membro (escritório) */ informadoPor: z.string().nullable(),
    confirmadoEm: z.string().nullable(), confirmadoPor: z.string().nullable(),
    contestadoEm: z.string().nullable(), contestadoPor: z.string().nullable(), contestadoMotivo: z.string().nullable(),
    desfeitoEm: z.string().nullable(), desfeitoPor: z.string().nullable(),
    comprovanteAnexoId: Id.nullable(),
    atualizadaPedidaEm: z.string().nullable(), atualizadaPedidaPor: z.string().nullable(),
    /** o escritório já respondeu ao pedido (mensagem visível depois dele), ou a guia foi paga/substituída */ atualizadaAtendida: z.boolean(),
  }).nullable().optional(),
  /** só para guia (fatia 2b-2): a quem o cliente mandou a guia por e-mail, se o link foi aberto, quem cancelou */
  encaminhamentos: z.array(z.object({ id: Id, quando: z.string(), para: z.string(), paraNome: z.string().nullable(), porNome: z.string(), abertoEm: z.string().nullable(), canceladoEm: z.string().nullable(), canceladoPor: z.string().nullable(), expiraEm: z.string(), /** o e-mail ao terceiro: na fila · enviado · não saiu (com o erro) */ email: z.enum(['NA_FILA', 'ENVIADO', 'NAO_SAIU']).nullable().optional(), emailErro: z.string().nullable().optional() })).optional(),
});
export type Entrega = z.infer<typeof Entrega>;
/** "Não encontrei esse pagamento": o motivo vai ao cliente (e-mail e portal). */
export const PagamentoContestarBody = z.object({ motivo: z.string().trim().min(3).max(300) });
export type PagamentoContestarBody = z.infer<typeof PagamentoContestarBody>;

// ---- anexo ----
export const Anexo = z.object({
  id: Id, nome: z.string(), mime: z.string(), tamanho: z.number().int(), enviadoPorApelido: z.string().nullable(), criadoEm: z.string(),
  /** nome padronizado escolhido na tarefa (modelo de documento ou texto livre) */
  titulo: z.string().nullable().optional(),
  /** como o cliente vê no e-mail e no portal: o título, ou o que o sistema deduz */
  tituloCliente: z.string().optional(),
  /** publicado no portal do cliente */
  visivelCliente: z.boolean(),
  exigeCiencia: z.boolean(),
  exigeAssinatura: z.boolean().optional(),
  /** assinatura eletrônica concluída (o primeiro contato) */
  assinatura: z.object({ id: Id, contatoNome: z.string(), quando: z.string(), ip: z.string().nullable() }).nullable().optional(),
  /** quem deu ciência no portal (o primeiro contato) */
  ciencia: z.object({ contatoNome: z.string(), quando: z.string(), ip: z.string().nullable(), origem: z.enum(['PORTAL', 'LINK']).optional() }).nullable(),
  enviadoEmailEm: z.string().nullable().optional(),
});
export type Anexo = z.infer<typeof Anexo>;
export const ANEXO_TAMANHO_MAX = 25 * 1024 * 1024;

// ---- comentário ----
export const Comentario = z.object({
  /** nulo quando quem escreveu foi o cliente */
  id: Id, membroId: Id.nullable(), apelido: z.string(), nome: z.string(), texto: z.string(), mencoes: z.array(Id), criadoEm: z.string(), editadoEm: z.string().nullable(),
  /** registro externo: o cliente vê no portal e recebe por e-mail; falso = nota interna da equipe */
  visivelCliente: z.boolean().optional(),
  de: z.enum(['ESCRITORIO', 'CLIENTE']).optional(),
  /** PEDIDO = mensagem da conversa do pedido que originou a tarefa */
  origem: z.enum(['TAREFA', 'PEDIDO']).optional(),
});
export type Comentario = z.infer<typeof Comentario>;
export const ComentarioBody = z.object({ texto: z.string().trim().min(1).max(4000), /** verdadeiro = mensagem ao cliente (portal + e-mail) */ visivelCliente: z.boolean().default(false) });

// ---- apontamento (horas) ----
export const Apontamento = z.object({
  id: Id, membroId: Id, apelido: z.string(), inicio: z.string(), fim: z.string().nullable(), minutos: z.number().int().nullable(), descricao: z.string().nullable(),
});
export type Apontamento = z.infer<typeof Apontamento>;
/** Lançamento manual: minutos + descrição (data opcional; padrão = agora). */
export const ApontamentoBody = z.object({ minutos: z.number().int().min(1).max(24 * 60), descricao: z.string().trim().max(300).nullable().default(null), data: DataISO.nullable().default(null) });
export const ApontamentoPararBody = z.object({ descricao: z.string().trim().max(300).nullable().default(null) });

// ---- escritório ----
export const EscritorioConfig = z.object({ nome: z.string(), cnpj: z.string().nullable(), exigirEntregaParaConcluir: z.boolean(),
  /** guia ou recibo registrado com arquivo vai ao cliente sozinho (envio por cliente e dia, com janela e "desfazer") */
  remessaAutomatica: z.boolean().optional(), retencaoAnos: z.number().int(), lembretesAtivos: z.boolean(), lembreteDias: z.array(z.number().int()), corPrimaria: z.string().nullable(), assinaturaEmail: z.string().nullable(), logoUrl: z.string().nullable(),
  /** ver senha / baixar certificado do cofre pede o código das duas etapas (vale 10 minutos) */
  cofreExigir2fa: z.boolean().optional(),
  /** todo membro precisa ter a verificação em duas etapas ligada */
  exigir2fa: z.boolean().optional(),
  /** para onde o cliente responde aos e-mails de envio (reply-to); departamento e pessoa podem sobrepor */
  emailResposta: z.string().nullable().optional(),
  /** clientes podem mandar uma guia a outro e-mail pelo portal (link de 7 dias, só o PDF) — o e-mail sai pelo sistema em nome do contato */
  encaminharGuias: z.boolean().optional() });
export type EscritorioConfig = z.infer<typeof EscritorioConfig>;
export const EscritorioConfigBody = z.object({ nome: z.string().trim().min(2).max(120).optional(), exigirEntregaParaConcluir: z.boolean().optional(), remessaAutomatica: z.boolean().optional(), encaminharGuias: z.boolean().optional(), retencaoAnos: z.number().int().min(1).max(30).optional(), lembretesAtivos: z.boolean().optional(), /** ≥ 0 = dias antes do vencimento; < 0 = dias depois, enquanto não paga (fatia 2c) */ lembreteDias: z.array(z.number().int().min(-30).max(30)).max(8).optional(), corPrimaria: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(), assinaturaEmail: z.string().trim().max(1000).nullable().optional(), cofreExigir2fa: z.boolean().optional(), exigir2fa: z.boolean().optional(), emailResposta: z.email().max(200).nullable().optional() });
/** Reemissão: a guia nova substitui a antiga (que sai do portal, dos lembretes e do e-mail; o link antigo avisa). */
export const EntregaSubstituirBody = z.object({ porEntregaId: Id });
export type EntregaSubstituirBody = z.infer<typeof EntregaSubstituirBody>;
